import React, { useEffect } from 'react';
import classnames from 'classnames';
import styles from './CreateDataProviderModal.module.scss';
import { DataVendor } from 'v2/models';
import { toast } from 'react-toastify';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import { FormControl, Spinner } from 'react-bootstrap';
import { useMutation } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import { getRootDomain } from 'v2/utils/formValidator';
import { uploadFile } from 'v2/utils/fileUtils';
import { useHistory } from 'react-router-dom';
import DataProviderForm, { DataProviderFormFields } from './DataProviderForm';
import { useForm, useWatch } from 'react-hook-form';
import useGetDataVendorByDomain from 'v2/hooks/useGetDataVendorByDomain';
import ExistingDataVendorBox from './ExistingDataProviderBox';
import ConfirmationModal from '../../ConfirmationModal/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import { TranslationKeys, PostProcessKeys } from 'translations';

interface CreateDataVendorModalProps {
  show: boolean;
  onClose?: () => void;
}

const CreateDataProviderModal = ({
  show,
  onClose,
}: CreateDataVendorModalProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset: resetForm,
    formState: { isValid },
  } = useForm<DataProviderFormFields>({ mode: 'onChange' });

  const domainValue = useWatch({ control, name: 'domain', defaultValue: '' });

  const {
    data: domainResult,
    isSuccess: isDomainLoadSuccess,
    isLoading,
  } = useGetDataVendorByDomain(domainValue);

  const { isLoading: isDataVendorMutateLoading, mutate: dataVendorMutate } =
    useMutation(
      async (data: DataProviderFormFields) => {
        if (data.avatar_url instanceof File) {
          const uploadResponse = await uploadFile(data.avatar_url);
          data.avatar_url = uploadResponse.url;
        }

        return await restFactory.post<DataVendor>(`/dj/api/v1/data_vendor/`, {
          ...data,
          domain: getRootDomain(data.domain),
        });
      },
      {
        onSuccess: (createdDataVendor: DataVendor) => {
          toast.success(`New ${t(TranslationKeys.dataProvider_one)} saved.`);
          history.push(
            `/data-provider-details/${createdDataVendor?.id}/detail`
          );
        },
        onError: () => {
          toast.error(`Error saving ${t(TranslationKeys.dataProvider_one)}.`);
        },
      }
    );

  useEffect(() => {
    if (isDomainLoadSuccess && domainResult?.isNew) {
      resetForm((formValues) => ({
        ...formValues,
        name: domainResult.dataVendor?.name || '',
        avatar_url: domainResult.dataVendor?.avatar_url || '',
        classification: domainResult.dataVendor?.classification || '',
        description: domainResult.dataVendor?.description || '',
        data_provider: domainResult.dataVendor?.data_provider || null,
      }));
    } else {
      resetForm((formValues) => ({
        ...formValues,
        name: '',
        avatar_url: '',
        classification: '',
        description: '',
        data_provider: null,
      }));
    }
  }, [domainResult, isDomainLoadSuccess]);

  const closeModal = () => {
    resetForm({
      domain: '',
      name: '',
      avatar_url: '',
      classification: '',
      description: '',
      data_provider: null,
    });
    onClose?.();
  };

  const onSubmit = (data: DataProviderFormFields) => {
    if (isValid) {
      dataVendorMutate(data);
    }
  };

  return (
    <ConfirmationModal
      show={show}
      title={`Add ${t(TranslationKeys.dataProvider_one, {
        postProcess: PostProcessKeys.TitleCase,
      })}`}
      onCancel={closeModal}
      isLoading={isDataVendorMutateLoading}
      onOk={handleSubmit(onSubmit)}
      okBtnDisabled={!isValid}
      okBtnText="Save"
      className={styles.viewModals}
      centered={true}
      animation={true}
      onEntered={() => {
        control._fields.domain?._f.ref.focus?.();
      }}
    >
      <>
        <p className={classnames(styles.modalInstructions)}>
          Start by entering the {t(TranslationKeys.company_one)} domain name.
        </p>

        <FormLabel
          label={`${t(TranslationKeys.company_one, {
            postProcess: PostProcessKeys.Capitalize,
          })} domain name`}
          required={false}
        />
        <div className="d-flex algin-items-center">
          <FormControl
            className={styles.inputText}
            type="text"
            autoComplete="off"
            placeholder="Domain Name"
            aria-label="domain_input"
            data-cy="datavendor:create-domain-field"
            {...register('domain', { required: true })}
          />
        </div>

        {isDomainLoadSuccess && domainResult && !domainResult.isNew ? (
          <ExistingDataVendorBox
            dataVendor={domainResult.dataVendor as DataVendor}
          />
        ) : (
          <div>
            {isLoading && (
              <div className="loadingIndicatorContainer">
                <Spinner animation="border" />
              </div>
            )}
            <DataProviderForm
              register={register}
              control={control}
              setValue={setValue}
              readOnly={!isDomainLoadSuccess}
            />
          </div>
        )}
      </>
    </ConfirmationModal>
  );
};

export default CreateDataProviderModal;
